/**
 * Explore's Share actions work on a real report, as in AgencyAnalytics: the
 * view on screen becomes an editable one-page client report named
 * "<Client> Google Analytics 4" (title, the cards, the line chart, the donut
 * and the table, with the same filters and period), listed with the
 * client's reports. Download PDF and Share Link then use that report.
 */

import type {
  Client,
  CompareSetting,
  Filter,
  Page,
  RangeSetting,
  Widget,
} from '../../reports/reports-api.ts';
import { reportsApi } from '../../reports/reports-api.ts';

const GRID = 12;
const CARD_W = 3;
const CARD_H = 2;
const CHART_H = 4;
const TABLE_H = 6;
const TABLE_ROWS = 50;

export interface ExploreSnapshot {
  client: Client;
  viewTitle: string;
  range: RangeSetting;
  compare: CompareSetting;
  timezone: string;
  filters: Filter[];
  cards: string[];
  selected: string;
  donut: string | null;
  table: { dimension: string; sort: { metric: string; direction: 'asc' | 'desc' } };
}

const block = (input: Omit<Widget, 'display'> & { title?: string }): Widget => ({
  type: input.type,
  x: input.x,
  y: input.y,
  w: input.w,
  h: input.h,
  data: input.data,
  display: input.title ? { title: input.title } : {},
});

function widgetsFor(view: ExploreSnapshot): Widget[] {
  const data = (extra: Partial<Widget['data']>): Widget['data'] => ({
    source: 'ga4',
    metrics: [view.selected],
    filters: view.filters,
    ...extra,
  });
  const cards = view.cards.map((key, index) =>
    block({
      type: 'METRIC',
      x: (index * CARD_W) % GRID,
      y: 1 + Math.floor((index * CARD_W) / GRID) * CARD_H,
      w: CARD_W,
      h: CARD_H,
      data: data({ metrics: [key] }),
    }),
  );
  const chartsY = 1 + Math.ceil((view.cards.length * CARD_W) / GRID) * CARD_H;
  const lineWidth = view.donut ? 8 : GRID;
  return [
    block({ type: 'HEADER', x: 0, y: 0, w: GRID, h: 1, data: {}, title: view.viewTitle }),
    ...cards,
    block({ type: 'LINE', x: 0, y: chartsY, w: lineWidth, h: CHART_H, data: data({}) }),
    ...(view.donut
      ? [
          block({
            type: 'DONUT',
            x: lineWidth,
            y: chartsY,
            w: GRID - lineWidth,
            h: CHART_H,
            data: data({ dimension: view.donut, limit: 8 }),
          }),
        ]
      : []),
    block({
      type: 'TABLE',
      x: 0,
      y: chartsY + CHART_H,
      w: GRID,
      h: TABLE_H,
      data: data({
        metrics: view.cards.slice(0, 8),
        dimension: view.table.dimension,
        sort: view.table.sort,
        limit: TABLE_ROWS,
      }),
    }),
  ];
}

/** Creates the client report for this view; resolves with its id. */
export async function createExploreReport(
  organisationId: string,
  view: ExploreSnapshot,
): Promise<string> {
  const name = `${view.client.name} Google Analytics 4`;
  const settings = {
    range: view.range,
    compare: view.compare,
    timezone: view.timezone,
    mode: 'LIVE' as const,
  };
  const created = await reportsApi.create(organisationId, {
    business_unit_id: view.client.id,
    kind: 'DOCUMENT',
    name,
    settings,
  });
  const page: Page = {
    title: view.viewTitle,
    settings: { layout: 'grid' },
    widgets: widgetsFor(view),
  };
  await reportsApi.save(organisationId, created.document.id, {
    revision: created.document.revision,
    name,
    settings,
    pages: [page],
    note: 'Created from GA4 Explore',
  });
  return created.document.id;
}
